// TODO: Tests TBD

import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useIntl } from 'react-intl';
import classnames from 'classnames';
import usePaymentMethodsSelector from '../../../hooks/usePaymentMethodsSelector/usePaymentMethodsSelector';
import PaymentForm from '../../../components/forms/SteppedPaymentForm';
import GiftCard from '../../../components/GiftCard';
import { paymentMethods } from '../../../slices/payments';
import PaymentMethodStepInfo from '../PaymentMethodStepInfo';
import { ShippingAddress } from '../../../components/forms/SteppedPaymentForm/types';
import messages from './messages';
import styles from './PaymentMethodsStep.module.scss';
import ErrorBanner from '../../../components/common/ErrorBanner';
import useSteppedCheckoutError from '../../../hooks/useSteppedCheckoutError';
import {
  focusAndScrollToByName,
  isSameAddressWithName,
  focusAndScrollTo,
} from '../../../utils';
import ThirdPartyService from '../../../services/third-party/third-party';
import { PaymentsInStore } from '../../../slices/payments/types';
import { CartInStore } from '../../../slices/cart/types';
import { CheckoutFormData } from '../SteppedCheckout/types';
import { OrderInStore } from '../../../slices/orders/types';
import { STRIPE_REGULAR_CHECKOUT_FEATURE_TOGGLE } from '../../../utils/common/constants';

const PaymentMethodsStep = ({
  showCompleted,
  isCompleted,
  showEditing,
  onClickSave,
  onClickCancel,
  shippingAddress,
  truemedButtonRef,
  acimaButtonRef,
  shopPayButtonRef,
  chargeAfterButtonRef,
  affirmButtonRef,
}: {
  showCompleted: boolean;
  isCompleted: boolean;
  showEditing: boolean;
  onClickSave: (data: CheckoutFormData) => Promise<void>;
  onClickCancel: () => Promise<void>;
  shippingAddress: ShippingAddress;
  truemedButtonRef?: React.RefObject<HTMLButtonElement> | null;
  acimaButtonRef?: React.RefObject<HTMLButtonElement> | null;
  shopPayButtonRef?: React.RefObject<HTMLDivElement> | null;
  chargeAfterButtonRef?: React.RefObject<HTMLButtonElement> | null;
  affirmButtonRef?: React.RefObject<HTMLDivElement> | null;
}) => {
  const { formatMessage } = useIntl();

  const { isPaymentMethodStepError, errorMessage } = useSteppedCheckoutError();
  const dispatch = useDispatch();

  interface AffirmData {
    checkout_token?: string;
  }

  interface PaymentMethodData {
    affirm?: AffirmData;
    cardData?: Record<string, unknown>;
    clientToken?: string;
    acima?: {
      checkoutToken?: string;
      leaseId?: string;
      leaseNumber?: string;
    };
  }
  type ValidateChangeParams = {
    isValid: boolean;
    errors: Record<string, string>;
  };

  const {
    applePaymentData,
    amazonCheckoutSessionId,
    selectedPaymentMethod,
    splititInstallmentPlanNumber,
    paymentMethodData,
    paypalOrderId,
    discoverCardError,
  } = useSelector<{
    payments: PaymentsInStore;
  }>((state) => state.payments) as {
    applePaymentData: string;
    amazonCheckoutSessionId: string;
    selectedPaymentMethod: string;
    splititInstallmentPlanNumber: number;
    paymentMethodData: PaymentMethodData;
    paypalOrderId: string;
    discoverCardError: boolean;
  };

  const { status: orderStatus } = useSelector<{ order: OrderInStore }>(
    (state) => state.order,
  ) as { status: string; id: string };

  const { billingAddress } = useSelector<{
    cart: CartInStore;
  }>((state) => state.cart) as {
    billingAddress?: undefined;
  };

  const [billingAddressErrors, setBillingAddressErrors] = useState<
    Record<string, string> | undefined
  >();
  const [isBillingAddressValid, setIsBillingAddressValid] = useState(true);
  const [isBillingAddressSame, setIsBillingAddressSame] = useState(true);

  const isOrderLoading = orderStatus === 'loading';
  const isCustomPaymentLoading = orderStatus === 'custom_loading';

  useEffect(() => {
    setIsBillingAddressSame(
      !billingAddress || isSameAddressWithName(billingAddress, shippingAddress),
    );
  }, [billingAddress, shippingAddress]);

  const [isTouched, setIsTouched] = useState(false);

  const isStripeRegularCheckoutEnabled = window.purTogglesEnabled(
    STRIPE_REGULAR_CHECKOUT_FEATURE_TOGGLE
  );

  const handleOnValidateChange = useCallback(
    ({ isValid, errors }: ValidateChangeParams) => {
      setIsBillingAddressValid(isValid);
      setBillingAddressErrors(errors);
    },
    [],
  );

  const { changeSelectedPaymentMethod, arePaymentMethodsDisabled } =
    usePaymentMethodsSelector();

  const handleSavePaymentForm = async (data: CheckoutFormData) => {
    if (discoverCardError) {
      setIsTouched(true);
      focusAndScrollTo('discoverPaymentNotice');
    } else if (
      !isStripeRegularCheckoutEnabled &&
      selectedPaymentMethod === paymentMethods.CREDIT_CARD &&
      !isBillingAddressValid &&
      !isBillingAddressSame
    ) {
      setIsTouched(true);
      if (billingAddressErrors) {
        focusAndScrollToByName(Object.keys(billingAddressErrors)[0]);
      }
    } else {
      await onClickSave(data);
    }
  };

  const getPreviouslyProcessedPaymentMethod = () => {
    if (paymentMethodData?.affirm?.checkout_token) {
      return paymentMethods.AFFIRM;
    }

    if (amazonCheckoutSessionId) {
      return paymentMethods.AMAZON_PAY;
    }

    if (applePaymentData) {
      return paymentMethods.APPLE_PAY;
    }

    if (splititInstallmentPlanNumber) {
      return paymentMethods.SPLIT_IT;
    }

    if (paypalOrderId) {
      return paymentMethods.PAYPAL;
    }

    if (paymentMethodData?.cardData && paymentMethodData?.clientToken) {
      return paymentMethods.CREDIT_CARD;
    }

    if (
      paymentMethodData?.acima?.checkoutToken &&
      paymentMethodData?.acima?.leaseId
    ) {
      return paymentMethods.ACIMA;
    }

    return undefined;
  };

  const cancelPaymentMethodHandler = () => {
    const previouslyConfirmedPaymentMethod =
      getPreviouslyProcessedPaymentMethod();

    if (previouslyConfirmedPaymentMethod) {
      changeSelectedPaymentMethod(previouslyConfirmedPaymentMethod);
    }

    onClickCancel();
  };

  const handleOpenZendChat = () => {
    ThirdPartyService.handleOpenChat();
  };

  const getPrettyPrintedPaymentMethod = (method: string) => 
    method.toLowerCase()
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase());

  return (
    <div>
      <div
        className={classnames(styles['payment-info-subtitle'], {
          [styles['payment-info-subtitle-complete']]: showCompleted,
          [styles['payment-info-subtitle-editing']]: showEditing,
        })}
      >
        {formatMessage(messages.subtitle)}
        <span className={styles['payment-info-subtitle-icon']} />
      </div>
      {showCompleted && !isOrderLoading && <PaymentMethodStepInfo />}
      <div
        className={classnames(styles['payment-form-container'], {
          [styles['hidden-button']]: !showEditing,
        })}
      >
        <ErrorBanner
          showError={isPaymentMethodStepError || discoverCardError}
          errorTitle={
            (isPaymentMethodStepError && formatMessage(messages.errorTitle)) ||
            (discoverCardError &&
              formatMessage(messages.discountCardErrorTitle)) ||
            ''
          }
          onCloseErrorMessage={() => dispatch({ type: 'removeError' })}
        >
          {isPaymentMethodStepError && errorMessage
            ? formatMessage(errorMessage)
            : ''}
          {discoverCardError && (
            <div
              className={classnames(styles.error)}
              id="discoverPaymentNotice"
            >
              {formatMessage(messages.discountCardErrorText1)}
              <button
                onClick={handleOpenZendChat}
                type="button"
                className={styles.chatNowLinkText}
              >
                {formatMessage(messages.chatNowLinkText)}
              </button>
              {formatMessage(messages.or)}
              <a href="tel:888-848-0965" className={styles.callUsLinkText}>
                {formatMessage(messages.callUsLinkText)}
              </a>
              {formatMessage(messages.discountCardErrorText2)}
            </div>
          )}
        </ErrorBanner>
        {isCustomPaymentLoading && (
          <div className={styles['processing-payment']}>
            <div className={styles['processing-payment-icon']} />
            <div className={styles['processing-payment-text']}>
              {formatMessage(messages.loadingCustomPayment, {
                paymentMethod: getPrettyPrintedPaymentMethod(selectedPaymentMethod)
              })}
            </div>
          </div>
        )}
        {isOrderLoading ? (
          <div className={styles['processing-payment']}>
            <div className={styles['processing-payment-icon']} />
            <div className={styles['processing-payment-text']}>
              {formatMessage(messages.processingPayment)}
            </div>
          </div>
        ) : (
          <div
            className={classnames({
              [styles.hidden]: isCustomPaymentLoading,
            })}
          >
            <PaymentForm
              selectedPaymentMethod={selectedPaymentMethod}
              onChangeSelectedPaymentMethod={changeSelectedPaymentMethod}
              paymentMethodsDisabled={arePaymentMethodsDisabled}
              handleSavePaymentForm={handleSavePaymentForm}
              handleOnValidateChange={handleOnValidateChange}
              shippingAddress={shippingAddress}
              billingAddress={billingAddress}
              isTouched={isTouched}
              isCompleted={isCompleted}
              cancelPaymentMethodHandler={cancelPaymentMethodHandler}
              truemedButtonRef={truemedButtonRef}
              acimaButtonRef={acimaButtonRef}
              shopPayButtonRef={shopPayButtonRef}
              chargeAfterButtonRef={chargeAfterButtonRef}
              affirmButtonRef={affirmButtonRef}
            />

            <div
              className={classnames(styles['giftcard-container'])}
            >
              <GiftCard />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};
export default PaymentMethodsStep;
